
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SendCodeButton } from "./SendCodeButton";
import { VerificationCodeInput } from "./VerificationCodeInput";
import { VerificationActions } from "./VerificationActions";

interface PhoneVerificationDialogProps {
  phoneNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
}

export function PhoneVerificationDialog({ phoneNumber, isOpen, onClose, onVerified }: PhoneVerificationDialogProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const { toast } = useToast();

  const sendVerificationCode = async () => {
    if (!phoneNumber) {
      toast({
        title: "Phone number required",
        description: "Please enter your phone number first.",
        variant: "destructive",
      });
      return;
    }

    setSendingCode(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCodeSent(true);
      toast({
        title: "Verification code sent",
        description: `We've sent a 6-digit code to ${phoneNumber}. For demo purposes, the code is 123456.`,
      });
    } catch (error) {
      toast({
        title: "Failed to send verification code",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter the 6-digit verification code.",
        variant: "destructive",
      });
      return;
    }

    setVerifying(true);
    try {
      if (verificationCode === "123456") {
        const { data: user } = await supabase.auth.getUser();
        if (!user?.user?.id) throw new Error("User not authenticated");

        console.log("Verifying phone number for user:", user.user.id, phoneNumber);

        const { error } = await supabase
          .from("profiles")
          .update({ 
            phone_verified: true,
            phone_number: phoneNumber // Make sure phone number is also stored
          })
          .eq("id", user.user.id);

        if (error) throw error;

        toast({
          title: "Phone verified",
          description: "Your phone number has been successfully verified.",
        });
        
        onVerified();
        onClose();
      } else {
        toast({
          title: "Invalid code",
          description: "The verification code is incorrect. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (value: string) => {
    setVerificationCode(value);
  };

  const handleResend = () => {
    setCodeSent(false);
    setVerificationCode("");
  };

  // Reset code when dialog closes or opens
  useEffect(() => {
    if (!isOpen) {
      setVerificationCode("");
      setCodeSent(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Verify your phone number</DialogTitle>
          <DialogDescription>
            We'll send a 6-digit code to {phoneNumber} to verify your phone number.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-6 py-4">
          {!codeSent ? (
            <SendCodeButton 
              onClick={sendVerificationCode} 
              isLoading={sendingCode} 
            />
          ) : (
            <>
              <VerificationCodeInput
                value={verificationCode}
                onChange={handleChange}
                codeSent={codeSent}
              />
              <VerificationActions
                onResend={handleResend}
                onVerify={verifyCode}
                isVerifying={verifying}
                canVerify={verificationCode.length === 6}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
